import { Trans, useTranslation } from 'next-i18next'
import { without } from 'ramda'

import {
  Heading1,
  LinkGrid,
  LinkText,
  Paragraph,
  Section,
  Section1,
  Section2
} from '@/features/Listings/seo/elements'
import PopularFilters from '@/features/Listings/seo/search-results-content/content/popular-filters'
import { Label } from '@/components/text'

import { getPopularCitiesByState, toTitleCase } from './utils'

const amenityTypes = [
  'washer-dryer',
  'air-conditioning',
  'dishwasher',
  'parking',
  'balcony',
  'gym-fitness-center',
  'swimming-pool',
  'wheelchair-access'
]

const propertyTypes = [
  'apartments',
  'townhouses',
  'houses',
  'rooms',
  'studios',
  '1-bedroom',
  '2-bedroom',
  '3-bedroom',
  '4-bedroom'
]

export const isAmenityFilterPage = (slug: string) => {
  if (!slug) return false
  return amenityTypes.some(amenity => slug.includes(amenity))
}

const AmenitiesFilterContent = ({
  state,
  citySlug,
  cityName,
  filterTitle,
  filterSlug,
  isCanadaPage,
  hasFilter,
  filterName,
  cityNameWithState
}: {
  state: string
  citySlug: string
  cityName: string
  filterTitle: string
  filterSlug: string
  isCanadaPage: boolean
  hasFilter: boolean
  filterName: string
  cityNameWithState: string
}) => {
  const { t } = useTranslation()
  const cityLinks = getPopularCitiesByState(state)

  const amenityContent: Record<
    string,
    { primary: { key: string; default: string }; secondary: string }
  > = {
    'washer-dryer': {
      primary: {
        key: 'seo:laundry filter paragraph',
        default:
          'Laundry day can be a hassle without a washer and dryer in your home. That’s why you’re searching for {{ filterTitle }} with insuite laundry in {{ cityName }}, {{ stateName }}! Having laundry facilities right in your unit makes life easier—no more collecting coins, hauling clothes to a laundromat, or waiting for shared machines.'
      },
      secondary: t(
        'seo:laundry filter paragraph 2',
        'Your next home with the convenience of in-unit laundry is just a click away!'
      )
    },
    'air-conditioning': {
      primary: {
        key: 'seo:air-conditioning filter paragraph',
        default:
          'Dealing with the summer heat can be tough without proper cooling. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! Having air conditioning in your home means you can stay comfortable year-round, no matter how high the temperatures rise.'
      },
      secondary: t(
        'seo:air-conditioning filter paragraph 2',
        'Your next home with the comfort of AC is just a click away!'
      )
    },
    dishwasher: {
      primary: {
        key: 'seo:dishwasher filter paragraph',
        default:
          'Doing dishes by hand can be time-consuming and tedious. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! Having a dishwasher in your home saves time, reduces hassle, and keeps your kitchen clean with ease.'
      },
      secondary: t(
        'seo:dishwasher filter paragraph 2',
        'Your next home with the convenience of a dishwasher is just a click away!'
      )
    },
    parking: {
      primary: {
        key: 'seo:parking filter paragraph',
        default:
          'Finding a place to park shouldn’t be a daily struggle. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! Having dedicated parking ensures convenience, security, and peace of mind—no more circling the block or worrying about street parking restrictions.'
      },
      secondary: t(
        'seo:parking filter paragraph 2',
        'Your next home with the ease of parking is just a click away!'
      )
    },
    balcony: {
      primary: {
        key: 'seo:balcony filter paragraph',
        default:
          'Having a private outdoor space can make all the difference in your home. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! Whether you want to enjoy your morning coffee, get fresh air, or unwind with a view, a balcony adds comfort and convenience to your living space.'
      },
      secondary: t(
        'seo:balcony filter paragraph 2',
        'Your next home with a balcony is just a click away!'
      )
    },
    'gym-fitness-center': {
      primary: {
        key: 'seo:gym-fitness-center filter paragraph',
        default:
          'Staying active and healthy is easier when you have a gym right where you live. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! Having an on-site fitness center means you can work out on your schedule—no need for expensive memberships or long commutes.'
      },
      secondary: t(
        'seo:gym-fitness-center filter paragraph 2',
        'Your next home with a gym is just a click away!'
      )
    },
    'swimming-pool': {
      primary: {
        key: 'seo:swimming-pool filter paragraph',
        default:
          'Nothing beats the luxury of having a pool right at home. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! Whether you want to cool off on a hot day, stay active with laps, or just relax by the water, a swimming pool adds a refreshing touch to your living space.'
      },
      secondary: t(
        'seo:swimming-pool filter paragraph 2',
        'Your next home with a swimming pool is just a click away!'
      )
    },
    'wheelchair-access': {
      primary: {
        key: 'seo:wheelchair-access filter paragraph',
        default:
          'Finding a home that meets your accessibility needs is essential. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! A wheelchair-accessible home ensures comfort, ease of movement, and a space designed to fit your lifestyle.'
      },
      secondary: t(
        'seo:wheelchair-access filter paragraph 2',
        'Your next accessible home is just a click away!'
      )
    }
  }

  const amenityFilter =
    amenityTypes.find(slug => filterSlug?.includes(slug)) || null

  const propertyFilter =
    propertyTypes.find(slug => filterSlug?.includes(slug)) || null

  const propertyString = t(
    `filters:b.listing.view.${propertyFilter || 'apartments'}.text`
  )

  if (!amenityFilter) return null

  return (
    <Section>
      {!isCanadaPage && (
        <PopularFilters
          isCanadaPage={isCanadaPage}
          citySlug={citySlug}
          cityName={cityName}
          cityNameWithState={cityNameWithState}
          hasFilter={hasFilter}
          filterName={filterName}
          customContent={
            <>
              <Paragraph>
                <Trans
                  i18nKey={amenityContent[amenityFilter].primary.key}
                  values={{
                    filterTitle: filterTitle?.toLowerCase(),
                    cityName,
                    stateName: state
                  }}
                  defaults={amenityContent[amenityFilter].primary.default}
                />
              </Paragraph>
              <Paragraph>
                {t(
                  'seo:Browse available rentals to see photos, floor plans, and details on amenities, neighborhoods, transit options, and more.'
                )}{' '}
                {amenityContent[amenityFilter].secondary}
              </Paragraph>
            </>
          }
        />
      )}
      <Section1>
        <Section2>
          <Heading1>
            {isCanadaPage ? (
              <Trans
                i18nKey="seo:Popular Amenities in Canada"
                values={{
                  filterTitle: toTitleCase(filterTitle)
                }}
                defaults="{{ filterTitle }} in Canada"
              />
            ) : (
              <Trans
                i18nKey="seo:Popular Amenities Nearby"
                values={{
                  filterTitle: toTitleCase(filterTitle)
                }}
                defaults="Nearby {{ filterTitle }}"
              />
            )}
          </Heading1>
        </Section2>
        <LinkGrid linksOnly>
          {without([citySlug], cityLinks).map(slug => {
            const name = t(`cities:city_name_${slug}`)
            return (
              <Label key={slug} fontSize="14px">
                <LinkText href={`/rental-listings/city/${slug}/${filterSlug}`}>
                  {isCanadaPage ? (
                    <Trans
                      i18nKey="seo:amenity for rent in city"
                      values={{
                        filterTitle,
                        cityName: name
                      }}
                      defaults="{{ filterTitle }} in {{ cityName }}"
                    />
                  ) : (
                    <Trans
                      i18nKey="seo:amenity for rent in city state"
                      values={{
                        filterTitle,
                        cityName: name,
                        stateName: state
                      }}
                      defaults="{{ filterTitle }} in {{ cityName }}, {{ stateName }}"
                    />
                  )}
                </LinkText>
              </Label>
            )
          })}
        </LinkGrid>
      </Section1>
      <Section1>
        <Section2>
          <Heading1>
            <Trans
              i18nKey="seo:amenities for rent in city"
              values={{
                propertyType: propertyString,
                cityNameWithState
              }}
              defaults="{{ propertyType }} for Rent in {{ cityNameWithState }} with Your Preferred Amenity"
            />
          </Heading1>
        </Section2>
        <LinkGrid linksOnly>
          {without([amenityFilter], amenityTypes).map(slug => {
            const amenityString = t(`filters:b.listing.view.${slug}.text`)
            return (
              <Label key={slug} fontSize="14px">
                <LinkText
                  href={`/rental-listings/city/${citySlug}/${
                    propertyFilter ? `${propertyFilter}-${slug}` : slug
                  }/`}
                >
                  <Trans
                    i18nKey="seo:property type for rent in city with amenity"
                    values={{
                      propertyType: propertyString,
                      cityNameWithState,
                      amenity: amenityString?.toLowerCase()
                    }}
                    defaults="{{ propertyType }} for rent in {{ cityNameWithState }} with {{ amenity }}"
                  />
                </LinkText>
              </Label>
            )
          })}
        </LinkGrid>
      </Section1>
    </Section>
  )
}

export default AmenitiesFilterContent
